import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/WarningRounded";
import CarIcon from "@material-ui/icons/DirectionsCar";
import DriverLicenseIcon from "@material-ui/icons/AssignmentInd";
import { Button, Typography } from "@packages/ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import getCountryFromURL from "src/utils/getCountryFromUrl";
import getBrandFromUrl from "src/utils/getBrandFromUrl";
import { ATHENA_AUTH } from "src/types/actions";
import * as virActions from "src/actions/virActions";
import useStyles from "src/layouts/DigitalVIRLayout/useStyles";
import { useIsMount } from "src/hooks/useIsMount";
import brandColors from "@packages/ui/theme/colors";

const DigitalVIRLandingPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const athenaAuth = useSelector((state) => state.vir.athenaAuth);
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get("source") === "app" ? "staff" : "customer");
  const isMount = useIsMount();
  const brand = getBrandFromUrl().toLowerCase();

  const athenaLogin = () => {
    const country = getCountryFromURL();
    const payload = {
      servicename: "shift",
      country: country == "AU" ? "au" : "nz",
      role: "customer",
    };
    dispatch({ type: ATHENA_AUTH, payload: payload });
    dispatch(
      virActions.virRole({
        role: role,
      })
    );
  };

  useEffect(() => {
    if (!isMount) {
      if (athenaAuth.authorization) {
        if (searchParams.get("source") === "app") {
          dispatch(
            virActions.virSource({
              source: "app",
            })
          );
          dispatch(
            virActions.virRole({
              role: "staff",
            })
          );
          navigate(
            `/vehicleincidents/vir-login?source=app&rego=${searchParams.get("rego")}${
              searchParams.get("raNumber") ? `&raNumber=${searchParams.get("raNumber")}` : ""
            }${
              searchParams.get("nonrevNumber")
                ? `&nonrevNumber=${searchParams.get("nonrevNumber")}`
                : ""
            }${searchParams.get("vir") ? `&vir=${searchParams.get("vir")}` : ""}${
              searchParams.get("virType") ? `&virType=${searchParams.get("virType")}` : ""
            }&brand=${brand}`,
            { replace: true }
          );
        } else if (searchParams.get("source") === "sms") {
          dispatch(
            virActions.virSource({
              source: "sms",
            })
          );
          const raNumber = searchParams.get("raNumber")?.toString() || "";
          const nonrevNumber = searchParams.get("nonrevNumber")?.toString() || "";
          navigate(
            `/vehicleincidents/vir-login?source=sms&rego=${searchParams.get(
              "rego"
            )}&vir=${searchParams.get("vir")}&virType=${searchParams.get(
              "virType"
            )}&brand=${brand}${raNumber !== "" ? `&raNumber=${raNumber}` : ""}${
              nonrevNumber !== "" ? `&nonrevNumber=${nonrevNumber}` : ""
            }`,
            { replace: true }
          );
        } else {
          navigate(`/vehicleincidents/vir-login?brand=${brand}`);
        }
      }
    }
  }, [athenaAuth]);

  useEffect(() => {
    // clean all the cache at first
    if (searchParams.get("source") === "app" || searchParams.get("source") === "sms") {
      dispatch(virActions.virInitAppAndSms());
      athenaLogin();
    } else {
      dispatch(virActions.virLogout());
    }
  }, []);

  return (
    <>
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          fontFamily: "Ride",
          color: "#333",
          margin: "0 auto",
        }}
      >
        <div className={classes.flexContainer}>
          <h1 className={classes.title}>Vehicle Incident Report</h1>
        </div>
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          style={{ maxWidth: "430px", margin: "0 auto", width: "90%" }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ paddingTop: "20px" }}
          >
            <WarningIcon color="primary" style={{ fontSize: "55px" }} />
            <Typography
              className={classes["landing-page-text"]}
              variant="body1"
              style={{
                marginLeft: "8px",
                textAlign: "center",
              }}
            >
              If you&#39;ve been involved in an accident, make sure everyone is safe and dial 000 if
              emergency services are required.
            </Typography>
          </Box>
        </Box>
        <br></br>
        <hr className={classes.breakLine} style={{ borderColor: brandColors[brand] }} />
        <br></br>
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          style={{ maxWidth: "380px", margin: "0 auto", width: "90%" }}
        >
          <Typography
            className={classes["landing-page-text"]}
            variant="body1"
            style={{ textAlign: "center", marginTop: "10px", marginBottom: "20px" }}
          >
            If you are at the scene of an incident, please collect the below information and return
            to the form when safe to do so.
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ paddingTop: "20px" }}
          >
            <CarIcon color="primary" style={{ fontSize: "40px" }} />
            <Typography
              className={classes["landing-page-text"]}
              variant="body1"
              style={{
                marginLeft: "8px",
                textAlign: "start",
              }}
            >
              Photos of any vehicles involved, as well as the incident site
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ paddingTop: "20px" }}
          >
            <DriverLicenseIcon color="primary" style={{ fontSize: "40px" }} />
            <Typography
              className={classes["landing-page-text"]}
              variant="body1"
              style={{
                marginLeft: "8px",
                textAlign: "start",
              }}
            >
              Contact details, or photos of driver licences&#39; for any witnesses, or other drivers
              involved in the accident
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="large"
          disabled={athenaAuth.loading}
          onClick={athenaLogin}
          style={{
            textTransform: "none",
            marginTop: "30px",
            fontFamily: "Ride",
            borderRadius: "25px",
          }}
        >
          {athenaAuth.loading ? <CircularProgress /> : "Start Form"}
        </Button>
      </Box>
    </>
  );
};

export default DigitalVIRLandingPage;
